import React, { useState, useEffect, forwardRef } from 'react';
import {
  Currencies,
  CURRENCY,
  displayMoney,
  get_precision,
  get_symbol,
  Money,
  OptionalMoney,
} from '../../../utils/money';
import {
  FormControl,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Box,
  IconButton,
  Button,
  Alert,
} from '@mui/material';
import Decimal from 'decimal.js';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { isMobile, isTablet } from 'react-device-detect';

interface MoneyInputProps {
  money: Money[];
  setMoney: (money: Money[]) => void;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  footer?: JSX.Element;
  hidden?: boolean;
}

function MoneyInput({
  money,
  setMoney,
  label,
  icon,
  disabled,
  footer,
  hidden
}: MoneyInputProps) {
  const [availableCurrencies, setAvailableCurrencies] = useState<CURRENCY[]>(
    Currencies.filter((item) => money.filter((m) => m[0] == item).length === 0)
  );
  const [optionalMoney, setOptionalMoney] = useState<OptionalMoney[]>(
    money.sort((a, b) => b[0].localeCompare(a[0]))
  );

  console.log('optional money = ', optionalMoney);
  const [rawValues, setRawValues] = useState<string[]>(
    money.map((m) => m[1].toString())
  );
  const [errors, setErrors] = useState<string[]>(Array(money.length).fill(''));

  useEffect(() => {
    setRawValues(optionalMoney.map((m) => (m[1] ? m[1].toString() : '')));
    const allValid = optionalMoney.every((item) => item[1] !== null);
    if (allValid) {
      setMoney(optionalMoney as Money[]);
    }
  }, [JSON.stringify(optionalMoney)]);

  useEffect(() => {
    setOptionalMoney(money.sort((a, b) => b[0].localeCompare(a[0])));
    setErrors(Array(money.length).fill(''));
  }, [displayMoney(money)]);

  const validateInput = (value: string): boolean => {
    value = value.replaceAll(',', '.');

    if (value === '' || value === '-' || value === '.' || value === '-.') {
      return false;
    }

    const numberRegex = /^-?\d*\.?\d*$/;
    if (!numberRegex.test(value)) {
      return false;
    }

    try {
      new Decimal(value);
      return true;
    } catch {
      return false;
    }
  };

  const handleAmountChange = (index: number, rawInput: string) => {
    const cleaned = rawInput.replaceAll(',', '.');
    const newRawValues = [...rawValues];
    newRawValues[index] = rawInput; // keep original user input
    setRawValues(newRawValues);

    // Try to validate
    if (validateInput(cleaned)) {
      const decimal = new Decimal(cleaned);
      const updatedMoney: OptionalMoney[] = optionalMoney.map(
        (item, i): OptionalMoney =>
          i === index
            ? [item[0], decimal.toDecimalPlaces(get_precision(item[0]))]
            : item
      );
      setOptionalMoney(updatedMoney);
      setMoney(updatedMoney.filter((m) => m[1] !== null) as Money[]);

      const newErrors = [...errors];
      newErrors[index] = '';
      setErrors(newErrors);
    } else {
      const newErrors = [...errors];
      newErrors[index] = rawInput.length > 0 ? 'Invalid amount' : 'Required';
      setErrors(newErrors);
    }
  };

  const handleBlur = (index: number, rawValues: string[]) => {
    const value = rawValues[index];
    const isValid = validateInput(value);

    if (isValid) {
      const decimal = new Decimal(value);
      const newMoney: OptionalMoney[] = optionalMoney.map((current, i) =>
        i === index
          ? [current[0], decimal.toDecimalPlaces(get_precision(current[0]))]
          : current
      );
      setOptionalMoney(newMoney);

      // Clear error if any
      const newErrors = [...errors];
      newErrors[index] = '';
      setErrors(newErrors);
    } else {
      // Clear the input and set error
      const newRawValues = [...rawValues];
      newRawValues[index] = '';
      const newMoney: OptionalMoney[] = optionalMoney.map((current, i) =>
        i === index ? [current[0], null] : current
      );
      console.log(
        'this is the thing: ',
        newRawValues,
        JSON.stringify(newMoney)
      );
      setRawValues((prev) => newRawValues);
      setOptionalMoney(newMoney);

      const newErrors = [...errors];
      newErrors[index] =
        rawValues[index].length > 0
          ? `${rawValues[index]} is not valid`
          : 'This field is required';
      setErrors(newErrors);
    }
  };

  const handleCurrencyChange = (index: number, newCurrency: CURRENCY) => {
    const currentValue = rawValues[index];
    const isValid = validateInput(currentValue);

    const decimalValue = isValid
      ? new Decimal(currentValue)
      : optionalMoney[index][1];

    const newVal =
      decimalValue?.toDecimalPlaces(get_precision(newCurrency)) ?? null;
    const newMoney: OptionalMoney[] = optionalMoney.map((current, i) =>
      i === index ? [newCurrency, newVal] : current
    );

    setAvailableCurrencies(
      [
        ...availableCurrencies.filter((item) => item != newCurrency),
        optionalMoney[index][0],
      ].sort((a, b) => b.localeCompare(a))
    );

    // Update raw value to formatted version
    const newRawValues = [...rawValues];
    newRawValues[index] = newVal ? newVal.toString() : '';
    setRawValues(newRawValues);

    setOptionalMoney(newMoney);
  };

  console.log(
    'here is money optional money for ',
    label,
    ' = ',
    JSON.stringify(optionalMoney)
  );
  console.log('here is money raw values for ', label, ' = ', rawValues);

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid #ccc',
        borderRadius: 1,
        padding: 2,
        pt: 4,
      }}
    >
      {/* Floating Label */}
      <Box
        sx={{
          position: 'absolute',
          top: -12,
          left: 16,
          backgroundColor: 'white',
          px: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: 'bold',
          color: '#888',
          fontSize: '0.875rem',
        }}
      >
        {icon && <Box component="span">{icon}</Box>}
        {label}
      </Box>

      {
        hidden ? <Alert severity="warning">Permission Denied to this resource</Alert> :
        <>
      {optionalMoney.map((item, index) => {
        return (
          <FormControl
            fullWidth
            key={index}
            sx={{ marginBottom: 2 }}
            style={{ minWidth: 200 }}
            error={errors[index].length > 0}
            required
          >
            <TextField
              autoComplete="off"
              onFocus={(e) =>
                e.target.addEventListener('wheel', (e) => e.preventDefault(), {
                  passive: false,
                })
              }
              required
              onBlur={() => handleBlur(index, rawValues)}
              disabled={disabled}
              error={errors[index].length > 0}
              placeholder={errors[index]}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FormControl variant="standard" sx={{ minWidth: 40 }}>
                      <Select
                        disabled={disabled}
                        value={item[0]}
                        onChange={(e) =>
                          handleCurrencyChange(
                            index,
                            e.target.value as CURRENCY
                          )
                        }
                        autoWidth
                        input={<OutlinedInput />}
                        sx={{
                          height: 40,
                          fontSize: '0.875rem',
                          borderRadius: 0,
                          '& .MuiSelect-icon': {
                            right: 4,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {[...availableCurrencies, item[0]]
                          .sort((a, b) => b.localeCompare(a))
                          .map((curr) => (
                            <MenuItem key={curr} value={curr}>
                              {get_symbol(curr)}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <DeleteIcon
                      sx={
                        disabled
                          ? {}
                          : {
                              cursor: 'pointer',
                              color: 'gray',
                              '&:hover': {
                                color: 'red',
                              },
                            }
                      }
                      onClick={() => {
                        if (!disabled) {
                          setAvailableCurrencies(
                            [...availableCurrencies, item[0]].sort((a, b) =>
                              b.localeCompare(a)
                            )
                          );
                          setOptionalMoney(
                            optionalMoney.filter((_, i) => i !== index)
                          );
                        }
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              type="text"
              value={rawValues[index]}
              inputProps={{
                maxLength: 13,
              }}
              onChange={(e) => {
                handleAmountChange(index, e.target.value);
              }}
              variant="outlined"
              helperText={errors[index] ? errors[index] : ''}
            />
          </FormControl>
        );
      })}

      {/* "Add More" Button below the inputs */}
      {availableCurrencies.length > 0 && !disabled && (
        <Box sx={{ textAlign: 'center', display: 'absolute', bottom: 0 }}>
          <Button
            size={(isMobile && !isTablet) ? 'small' : 'medium'}
            variant="outlined"
            color="primary"
            disabled={disabled}
            onClick={() => {
              setAvailableCurrencies(availableCurrencies.slice(1));
              setOptionalMoney([
                ...optionalMoney,
                [availableCurrencies[0], null],
              ]);
              setRawValues([...rawValues, '']);
              setErrors([...errors, 'This field is required']);
            }}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 500000,
            }}
          >
            <AddIcon />
          </Button>
        </Box>
      )}
      {footer && footer}
      </>}
    </Box>
  );
}

export default MoneyInput;
